/**
 * Copyright 2023-2024 Highway9 Networks Inc.
 */
import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "..";
import { siteService } from "../../services/site-service";
import { ISite } from "../../types/site";
import {
  ChannelAllocationStrategy,
  CHANNEL_ALLOCATION_STRATEGY_STATIC_PAIRS,
} from "../../types/channelPool";
import { edgeEdgesWithPool, edgeEntities } from "./edgeSlice";
import { settingsSystemEntities } from "./settingsSlice";
import { siteEdgesHave5G } from "~/helpers/siteEdgeHelpers";
import { createSocketPatchReducer } from "../events/socketPatch";

export const sitesAdapter = createEntityAdapter<ISite>({
  sortComparer: (a, b) => a.name.localeCompare(b.name),
});

interface SiteState {
  open: boolean;
  isEdit: boolean;
  selectedId: string | null;
  sitesData: EntityState<ISite>;
  fetchDone: boolean;
  expanded: string[];
  dashboardSelectedId: string | null;
  loading: boolean;
  carrierKPIExport: {
    open: boolean;
    operationID: string | null;
    isRunning: boolean;
  };
  exportCiqOpen: boolean;
  importSiteOpen: boolean;
}

const initialState: SiteState = {
  open: false,
  isEdit: false,
  selectedId: null,
  sitesData: sitesAdapter.getInitialState(),
  fetchDone: false,
  expanded: [],
  dashboardSelectedId: null,
  loading: true,
  carrierKPIExport: {
    open: false,
    operationID: null,
    isRunning: false,
  },
  exportCiqOpen: false,
  importSiteOpen: false,
};

export const fetchSites = createAsyncThunk("site/fetchSites", async (query?: { timestamp?: number }) => {
  const timestamp = query?.timestamp;
  const queryString = timestamp ? { timestamp } : {};
  return siteService.getSites(queryString);
});

const siteSlice = createSlice({
  name: "site",
  initialState,
  reducers: {
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },
    setIsEdit: (state, action: PayloadAction<boolean>) => {
      state.isEdit = action.payload;
    },
    /** Store selection identity only; resolve the entity via selectors. */
    setSelected: (state, action: PayloadAction<string | null>) => {
      state.selectedId = action.payload;
    },
    setFetchDone: (state, action: PayloadAction<boolean>) => {
      state.fetchDone = action.payload;
    },
    setExpanded: (state, action: PayloadAction<string[]>) => {
      state.expanded = action.payload;
    },
    setDashboardSelectedId: (state, action: PayloadAction<string | null>) => {
      state.dashboardSelectedId = action.payload;
    },
    setCarrierKPIExportOpen: (state, action: PayloadAction<boolean>) => {
      state.carrierKPIExport.open = action.payload;
    },
    setCarrierKPIExportID: (state, action: PayloadAction<string | null>) => {
      state.carrierKPIExport.operationID = action.payload;
    },
    setCarrierKPIExportRunning: (state, action: PayloadAction<boolean>) => {
      state.carrierKPIExport.isRunning = action.payload;
    },
    exportCiqOpen: (state, action: PayloadAction<boolean>) => {
      state.exportCiqOpen = action.payload;
    },
    setImportSiteOpen: (state, action: PayloadAction<boolean>) => {
      state.importSiteOpen = action.payload;
    },
    applySocketPatches: createSocketPatchReducer(sitesAdapter, (s) => s.sitesData),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSites.fulfilled, (state, action) => {
        sitesAdapter.setAll(state.sitesData, action.payload);
        state.loading = false;
      })
      .addCase(fetchSites.rejected, (state, action) => {
        console.log(action);
        state.loading = false;
      });
  },
});

export const siteActions = siteSlice.actions;
export default siteSlice.reducer;

const siteSelectors = sitesAdapter.getSelectors(
  (state: RootState) => state.site.sitesData,
);

export const siteData = siteSelectors.selectAll;
export const siteEntities = siteSelectors.selectEntities;

export const siteState = createSelector(
  [(state: RootState) => state.site.selectedId, siteEntities],
  (selectedId, entities): ISite | null => (selectedId ? entities[selectedId] ?? null : null),
);

export const siteCurrentDashboard = createSelector(
  [(state: RootState) => state.site.dashboardSelectedId, siteEntities],
  (dashboardSelectedId, entities): ISite | null =>
    dashboardSelectedId ? entities[dashboardSelectedId] ?? null : null,
);

export const siteLoading = (state: RootState) => state.site.loading;
export const siteOpen = (state: RootState) => state.site.open;
export const siteEdit = (state: RootState) => state.site.isEdit;
export const fetchDone = (state: RootState) => state.site.fetchDone;
export const siteExpanded = (state: RootState) => state.site.expanded;
export const siteByID = (id: string) => (state: RootState) => siteSelectors.selectById(state, id);
export const siteExportCiqOpen = (state: RootState) => state.site.exportCiqOpen;
export const siteImportOpen = (state: RootState) => state.site.importSiteOpen;

export const siteEnbLists = createSelector([siteData], (sites) =>
  sites
    .map((site) => ({
      id: site.id,
      name: site.name,
      macroEnbIdList: site.macroEnbIdList,
    }))
    .filter((site) => site.macroEnbIdList?.length),
);

/**
 * Default Channel Allocation Strategy for new 4G sites — sourced from the
 * `Default.Channel.Allocation.Strategy` system setting, falling back to
 * STATIC_PAIRS when the setting is missing. The setting value already uses
 * the same upper-case enum as the per-site field, so no mapping is needed.
 */
export const defaultChannelAllocationStrategy = (state: RootState): ChannelAllocationStrategy => {
  const raw = settingsSystemEntities(state)["Default.Channel.Allocation.Strategy"]?.value;
  if (
    raw === "CA_CHANNEL_ALLOCATION_STRATEGY_STATIC_PAIRS" ||
    raw === "CA_CHANNEL_ALLOCATION_STRATEGY_SHARED_SECONDARY"
  ) {
    return raw;
  }
  return CHANNEL_ALLOCATION_STRATEGY_STATIC_PAIRS;
};

export const siteHas5GEdge = createSelector(
  [siteState, edgeEntities, edgeEdgesWithPool],
  (currentSite, edgesById, edgesWithPools) =>
    siteEdgesHave5G(currentSite?.edges, edgesById, edgesWithPools),
);
